import {
  CloseCircleFilled,
  ExportOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  AutoComplete,
  Flex,
  Input,
  Layout,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import BaseButton from 'components/atoms/baseButton';
import PapCaseDetailsModal from 'components/organisms/patient-medication/components/pap-case-list/pap-case-details-modal';
import { downloadPapForm } from 'components/organisms/patient-medication/hooks/use-pap-cases';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { Routes } from 'router/routes';
import useUser from 'utils/useUser';

import { usePapWorkplace } from './hooks/use-pap-workplace';
import s from './styles.module.scss';
import { isEditablePapCase } from '../patient-medication/hooks/helper';

import type { ColumnsType } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import type {
  IPapWorkplaceCase,
  PapCaseStatusValue,
  PapWorkplaceStatusFilter,
} from './types';

const { Header } = Layout;
const { Text } = Typography;

const STATUS_LABELS: Record<PapWorkplaceStatusFilter, string> = {
  all: 'All',
  inReview: 'In Review',
  needsInfo: 'Needs Info',
  submitted: 'Submitted',
  approved: 'Approved',
  denied: 'Denied',
};

const STATUS_COLORS: Record<PapCaseStatusValue, string> = {
  inReview: 'purple',
  needsInfo: 'orange',
  submitted: 'blue',
  approved: 'green',
  denied: 'red',
};

const STATUS_FILTER_TABS: PapWorkplaceStatusFilter[] = [
  'all',
  'inReview',
  'needsInfo',
  'submitted',
  'approved',
  'denied',
];

const PapWorkplace = () => {
  const { t } = useTranslation();
  const {
    cases,
    total,
    isLoading,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    inputValue,
    handleInputChange,
    handleSearchSelect,
    handleSearchClear,
    searchSuggestions,
    statusCounts,
    handleSortChange,
    selectedCase,
    handleOpenCase,
    handleCloseCase,
  } = usePapWorkplace();

  const { userType, roleNames } = useUser();

  const canEditPapCase = isEditablePapCase(userType, roleNames);

  const columns: ColumnsType<IPapWorkplaceCase> = [
    {
      title: t('labels.papCaseName'),
      dataIndex: 'caseName',
      key: 'caseName',
      width: '22rem',
      ellipsis: { showTitle: true },
      render: (caseName: string) => (
        <span className={s.papCaseNameText}>{caseName}</span>
      ),
    },
    {
      title: t('labels.patientName'),
      dataIndex: 'patientName',
      key: 'patientName',
      width: '16rem',
      ellipsis: true,
      render: (name: string, record: IPapWorkplaceCase) => (
        <Link
          to={Routes.patientGuid(record.patientId)}
          target="_blank"
          rel="noopener noreferrer"
          className={s.patientNameLink}
          onClick={(e) => e.stopPropagation()}
        >
          <span className={s.patientNameText}>{name}</span>
          <ExportOutlined className={s.exportIcon} />
        </Link>
      ),
    },
    {
      title: t('labels.lastUpdate'),
      dataIndex: 'lastUpdate',
      key: 'lastUpdate',
      width: '10rem',
      sorter: (a: IPapWorkplaceCase, b: IPapWorkplaceCase) =>
        new Date(a.lastUpdate).getTime() - new Date(b.lastUpdate).getTime(),
      render: (date: string) => {
        const formattedDate = DateFormatter({
          date,
          format: DateFormats['MM/dd/yyyy'],
        });
        const formattedTime = DateFormatter({
          date,
          format: DateFormats['hh:mm a'],
        });
        return (
          <div className={s.dateCell}>
            <span>{formattedDate}</span>
            <span className={s.dateTime}>{formattedTime}</span>
          </div>
        );
      },
    },
    {
      title: t('labels.specialist'),
      dataIndex: 'specialist',
      key: 'specialist',
      width: '8rem',
      sorter: true,
    },
    {
      title: t('labels.status'),
      dataIndex: 'status',
      key: 'status',
      width: '8rem',
      sorter: true,
      render: (status: PapCaseStatusValue) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
    },
    {
      title: t('labels.reviewerNotes'),
      dataIndex: 'note',
      key: 'note',
      width: '22rem',
      render: (note: string | null) =>
        note || (
          <Text className={s['no-note-text']} type="secondary">
            {t('labels.noNote')}
          </Text>
        ),
    },
  ];

  return (
    <Layout className={s.layout}>
      <Header className={s.breadcrumbBar}>
        <Flex
          align="center"
          justify="space-between"
          className={s.breadcrumbBarInner}
        >
          <Text className={s.pageTitle}>{t('labels.papWorkplace')}</Text>
          <AutoComplete
            value={inputValue}
            options={searchSuggestions}
            onSelect={handleSearchSelect}
            onChange={handleInputChange}
            className={s.searchInput}
            notFoundContent={null}
            open={inputValue.length > 0 ? undefined : false}
          >
            <Input
              placeholder={t('labels.searchPatientOrCaseName')}
              suffix={
                inputValue ? (
                  <CloseCircleFilled
                    className={s.searchClearIcon}
                    onClick={handleSearchClear}
                  />
                ) : (
                  <SearchOutlined className={s.searchIcon} />
                )
              }
            />
          </AutoComplete>
        </Flex>
      </Header>

      <div className={s.content}>
        <div className={s.tableWrapper}>
          <div className={s.filters}>
            {STATUS_FILTER_TABS.map((status) => (
              <BaseButton
                key={status}
                label={`${STATUS_LABELS[status]} (${statusCounts[status]})`}
                type="primary"
                className={
                  statusFilter === status
                    ? s.filterButton
                    : `${s.filterButton} ${s.filterButtonUnselected}`
                }
                onClick={() => setStatusFilter(status)}
              />
            ))}
          </div>

          <Table<IPapWorkplaceCase>
            dataSource={cases}
            columns={columns}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: currentPage,
              pageSize: 30,
              total,
              size: 'small',
              position: ['bottomRight'],
              onChange: (page) => setCurrentPage(page),
            }}
            onRow={(record) => ({
              onClick: () => handleOpenCase(record),
              className: s.clickableRow,
            })}
            onChange={(_pagination, _filters, sorter, extra) => {
              if (extra.action !== 'sort') return;
              const sortResult = sorter as SorterResult<IPapWorkplaceCase>;
              if (sortResult.columnKey === 'lastUpdate') return;
              handleSortChange(
                sortResult.order ? (sortResult.columnKey as string) : undefined,
                sortResult.order,
              );
            }}
          />

          {selectedCase && (
            <PapCaseDetailsModal
              open={!!selectedCase}
              patientGuid={selectedCase.patientId}
              papCaseGuid={selectedCase.id}
              onDownloadPapForm={downloadPapForm}
              onClose={handleCloseCase}
              isReviewerView
              isReadOnly={!canEditPapCase}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PapWorkplace;
